import React, { ForwardedRef } from 'react';
import clsx from 'clsx';

import styles from '@danske/sapphire-css/components/table/table.module.css';
import { DOMAttributes } from '@react-types/shared';
import { mergeProps } from '@react-aria/utils';
import { useTableContainerContext } from './TableContainerContext';
import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';

export interface TableHeaderProps
  extends Omit<
      DOMAttributes<HTMLTableSectionElement>,
      'className' | 'style' | 'dangerouslySetInnerHTML'
    >,
    SapphireStyleProps {
  /** If the table header should be sticky relative to the container.
   * when the {@Link Table.Container} has a prop stickyHeader, the value is overridden
   **/
  sticky?: boolean;
  /**
   * Note: Currently, we don't officially support non-table elements.
   *
   * @default 'thead'
   */
  elementType?: 'thead';

  children?: React.ReactNode;
}

/**
 * Corresponding to `thead` element in native html. Should be rendered inside
 * {@Link Table}. Accepts a set of {@Link Table.HeaderRow} as children
 */

export const TableHeader = React.forwardRef(function TableHeader(
  { children, elementType, sticky, ...props }: TableHeaderProps,
  ref: ForwardedRef<HTMLTableSectionElement>
) {
  const ElementType = elementType || 'thead';
  const role = props.role || ElementType === 'thead' ? undefined : 'rowgroup';

  const context = useTableContainerContext();

  const { styleProps, filteredProps } = useSapphireStyleProps(props);

  return (
    <ElementType
      ref={ref}
      {...mergeProps(filteredProps, { role })}
      className={clsx(
        styles['sapphire-table__head'],
        {
          [styles['sapphire-table__head--sticky']]:
            context?.shouldHeaderStick || sticky,
        },
        styleProps.className
      )}
      style={styleProps.style}
    >
      {children}
    </ElementType>
  );
});
